import React from 'react';
import { Link } from 'react-router-dom';

const CareerComparisonInfoPage: React.FC = () => {
  return (
    <div className="career-info-page">
      <div className="hero-section career">
        <h1>Career Comparison Calculator</h1>
        <p>Make informed decisions about your career path</p>
      </div>

      <div className="info-content">
        <div className="info-section">
          <h2>What This Calculator Does</h2>
          <p>
            The Career Comparison Calculator helps you evaluate the financial impact of two career paths:
            staying in your current role or pursuing additional education. It provides a comprehensive
            analysis of lifetime earnings, considering factors like tuition costs, opportunity costs,
            the job search after graduating, and tax implications.
          </p>
        </div>

        <div className="info-section">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <h3>💰 Real Dollar Analysis</h3>
              <p>Everything is in today's dollars. Raises you enter are above inflation, so the numbers mean what they say</p>
            </div>
            <div className="feature-card">
              <h3>🏛️ Provincial Tax Calculations</h3>
              <p>Accurate after-tax income based on your province of residence</p>
            </div>
            <div className="feature-card">
              <h3>📈 Customizable Growth</h3>
              <p>Set different raise expectations for each career path over 10 years</p>
            </div>
            <div className="feature-card">
              <h3>🎓 Education Cost Analysis</h3>
              <p>Tuition, income earned while studying, the federal tuition credit, and the job search after graduating</p>
            </div>
            <div className="feature-card">
              <h3>🧾 Year-by-Year Cash Flow</h3>
              <p>Every year from today to retirement: earnings, tax, school costs and running balance</p>
            </div>
            <div className="feature-card">
              <h3>⚖️ Break-Even Analysis</h3>
              <p>Shows exactly when your education investment pays off</p>
            </div>
            <div className="feature-card">
              <h3>🎯 Ready-Made Examples</h3>
              <p>Load a researched comparison, such as trades against university, then edit it</p>
            </div>
            <div className="feature-card">
              <h3>📉 Sensitivity Analysis</h3>
              <p>See how the answer moves if your raises come in weaker or stronger than planned</p>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h2>How It Works</h2>
          <div className="workflow">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Input Your Current Situation</h3>
                <p>Enter your age, current salary, benefits, and province for tax calculations</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Define Career Paths</h3>
                <p>Set expectations for both staying in your current role and pursuing education</p>
              </div>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Get Comprehensive Results</h3>
                <p>View lifetime earnings, break-even analysis, and personalized recommendations</p>
              </div>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h2>What You'll Learn</h2>
          <ul className="benefits-list">
            <li><strong>Total Lifetime Earnings:</strong> Compare after-tax earnings from today to retirement on both paths</li>
            <li><strong>Break-Even Point:</strong> See exactly when your education investment pays off</li>
            <li><strong>Risks and Opportunities:</strong> The five biggest arguments for and against the path with schooling</li>
            <li><strong>Tax Implications:</strong> See how provincial taxes affect your take-home pay</li>
            <li><strong>Opportunity Costs:</strong> Understand what you're giving up while in school</li>
            <li><strong>Long-term Growth:</strong> Compare salary progression in both scenarios</li>
            <li><strong>Out-of-Pocket Cost:</strong> What the schooling actually costs you after any income earned while studying</li>
            <li><strong>Sensitivity:</strong> How much later the break-even arrives if your raises disappoint</li>
          </ul>
        </div>

        <div className="info-section">
          <h2>Important Considerations</h2>
          <div className="considerations">
            <div className="consideration-item">
              <h3>🎯 Non-Financial Factors</h3>
              <p>
                While this calculator focuses on financial outcomes, consider other important factors
                like job satisfaction, work-life balance, career growth opportunities, and personal
                fulfillment when making your decision.
              </p>
            </div>
            <div className="consideration-item">
              <h3>📚 Education Quality</h3>
              <p>
                The calculator assumes you complete your education and find employment in your field.
                Consider the reputation of the institution and program you're considering.
              </p>
            </div>
            <div className="consideration-item">
              <h3>🌍 Market Conditions</h3>
              <p>
                The salaries and raises you enter reflect today's market. They may change over time,
                affecting the accuracy of long-term projections.
              </p>
            </div>
          </div>
        </div>

        {/* Cross-Tool Recommendations */}
        <div style={{padding: '3rem 2rem', background: '#f9fafb', margin: '0 -2rem'}}>
          <div style={{maxWidth: '900px', margin: '0 auto'}}>
            <h2 style={{textAlign: 'center', marginBottom: '0.5rem'}}>Explore More Financial Tools</h2>
            <p style={{textAlign: 'center', color: '#6b7280', marginBottom: '2rem'}}>One account, all the tools you need</p>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
              <Link to="/tax-tools" style={{textDecoration: 'none', color: 'inherit'}}>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                  <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Tax Tools</h3>
                  <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Estimate income taxes and calculate payroll deductions</p>
                </div>
              </Link>
              <Link to="/rrsp" style={{textDecoration: 'none', color: 'inherit'}}>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                  <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Retirement Planner</h3>
                  <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Plan your retirement with RRSP, TFSA, CPP, and OAS projections</p>
                </div>
              </Link>
              <Link to="/car-comparison" style={{textDecoration: 'none', color: 'inherit'}}>
                <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                  <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Car Calculator</h3>
                  <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Buy vs lease vs finance -- see the true cost of car ownership</p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h2>Ready to Compare?</h2>
          <p>
            Start analyzing your career options with our comprehensive calculator. Get detailed
            insights into the financial implications of your career decisions.
          </p>
          <Link to="/career-comparison" className="cta-button">
            Start Career Comparison
          </Link>
        </div>

        <div className="info-section">
          <h2>Need Help?</h2>
          <p>
            If you have questions about using the calculator or interpreting your results,
            our support team is here to help. You can also explore our other financial tools
            for comprehensive financial planning.
          </p>
          <div className="other-tools">
            <Link to="/housing" className="tool-link">🏠 Housing Calculator</Link>
            <Link to="/rrsp" className="tool-link">💰 RRSP Calculator</Link>
            <Link to="/forecast" className="tool-link">📊 Personal Forecast</Link>
            <Link to="/car-comparison" className="tool-link">🚗 Car Comparison</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerComparisonInfoPage;